import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import {
    Box,
    Button,
    Checkbox,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Image,
    Input,
    InputGroup,
    InputRightElement,
    Link,
    Stack,
    Text,
    useToast,
} from "@chakra-ui/react";

import { useLoginMutation } from "redux/services/apiService";
import { useGetConfigsQuery } from "redux/services/config";

import SliderCaptcha from "components/Captcha/SliderCaptcha";

import "../../utils/lib/slidercaptcha";

import '../../App.css'

type FormValues = {
    email: string;
    password: string;
};

const LoginForm = () => {
    const { t } = useTranslation();
    const toast = useToast();
    const [show, setShow] = React.useState(false);
    // ✅ state captcha
    const [validate, setValidate] = React.useState(false);
    const [isRobotChecked, setRobotChecked] = React.useState(false);
    const [isCaptchaVisible, setCaptchaVisible] = React.useState(false);
    const [disabledCheckbox, setDisabledCheckbox] = React.useState(false);

    const handleCheckboxChange = () => {
        // kalau sudah valid, jangan buka lagi
        if (validate) return;
        setCaptchaVisible(true);
        setDisabledCheckbox(true);
    };

    const handleCaptchaSuccess = () => {
        setValidate(true);
        setRobotChecked(true);
        setCaptchaVisible(false);
        setDisabledCheckbox(true);
    };

    const onSubmit = async (values: FormValues) => {
        try {
            // ✅ optional: blok submit kalau belum valid
            if (!validate) return;

            await loginMutation(values).unwrap();
            toast({
                title: t("login_successfully"),
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (e) {
            const msg =
                "Email atau kata sandi tidak sesuai. Silakan periksa kembali dan coba lagi";

            // bikin kedua input merah
            setError("email", { type: "server", message: " " });
            setError("password", { type: "server", message: msg });
        }
    };
    const { data: config } = useGetConfigsQuery();
    const [loginMutation] = useLoginMutation();

    const {
        handleSubmit,
        register,
        setError,
        clearErrors,
        formState: { errors, isSubmitting },
    } = useForm<FormValues>({
        defaultValues: { email: "", password: "" },
    });


    const logoUrl = config?.data?.[0]?.site_logo
        ? `${process.env.REACT_APP_API_URL}/${config.data[0].site_logo}`
        : null;

    return (
        <Flex
            flex="1"
            bg="white"
            direction="column"
            position="relative"
            px={{ base: 6, md: 14 }}
            py={{ base: 8, md: 10 }}
        >
            {/* Logo fixed di atas kiri (seperti figma) */}
            <Flex align="center" gap={3}>
                {logoUrl ? (
                    <Image src={logoUrl} alt="Logo" w="10" h="10" objectFit="contain" />
                ) : null}
                <Box>
                    <Text fontSize="xs" color="gray.600" fontWeight="medium">
                        {config?.data?.[0]?.site_name ?? ""}
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                        {config?.data?.[0]?.site_title ?? ""}
                    </Text>
                </Box>
            </Flex>

            {/* Area form ditengah */}
            <Flex flex="1" align="center" justify="center">
                <Box w="full" maxW="420px">
                    <Stack spacing={2} mb={8} textAlign="center">
                        <Text fontSize="lg" fontWeight="semibold" color="gray.800">
                            Selamat Datang
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                            Silakan masukkan alamat email dan kata sandi Anda untuk masuk ke akun
                        </Text>
                    </Stack>

                    <Stack as="form" spacing={4} onSubmit={handleSubmit(onSubmit)}>
                        <FormControl isInvalid={!!errors.email}>
                            <FormLabel fontSize="sm" color="gray.700" mb={2}>
                                Email
                            </FormLabel>
                            <Input
                                h="40px"
                                fontSize="sm"
                                placeholder="Contoh: nama@gmail.com"
                                borderColor={errors.email ? "red.400" : "gray.200"}
                                _hover={{ borderColor: errors.email ? "red.400" : "gray.300" }}
                                _focusVisible={{
                                    borderColor: errors.email ? "red.400" : "gray.300",
                                    boxShadow: "none",
                                }}
                                {...register("email", { onChange: () => clearErrors() })}
                            />
                        </FormControl>

                        <FormControl isInvalid={!!errors.password}>
                            <FormLabel fontSize="sm" color="gray.700" mb={2}>
                                Kata Sandi
                            </FormLabel>

                            <InputGroup>
                                <Input
                                    h="40px"
                                    fontSize="sm"
                                    type={show ? "text" : "password"}
                                    placeholder="Masukkan kata sandi Anda"
                                    pr="44px"
                                    borderColor={errors.password ? "red.400" : "gray.200"}
                                    _hover={{ borderColor: errors.password ? "red.400" : "gray.300" }}
                                    _focusVisible={{
                                        borderColor: errors.password ? "red.400" : "gray.300",
                                        boxShadow: "none",
                                    }}
                                    {...register("password", { onChange: () => clearErrors() })}
                                />

                                <InputRightElement h="40px" w="44px">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setShow((v) => !v)}
                                        minW="auto"
                                        h="40px"
                                        px={2}
                                        color="gray.500"
                                        _hover={{ color: "gray.700", bg: "transparent" }}
                                        _active={{ bg: "transparent" }}
                                        _focusVisible={{ boxShadow: "none" }}
                                        aria-label={show ? "Hide password" : "Show password"}
                                    >
                                        {show ? <ViewOffIcon boxSize={5} /> : <ViewIcon boxSize={5} />}
                                    </Button>
                                </InputRightElement>
                            </InputGroup>

                            <FormErrorMessage
                                mt={3}
                                bg="#FDECEC"          // merah muda seperti gambar
                                color="#D64545"       // merah teks
                                px={3}
                                py={2}
                                borderRadius="md"
                                fontSize="sm"
                                lineHeight="short"
                            >
                                {errors.password?.message}
                            </FormErrorMessage>
                        </FormControl>

                        <Checkbox
                            size="sm"
                            colorScheme="gray"
                            isChecked={isRobotChecked}
                            isDisabled={disabledCheckbox}
                            onChange={handleCheckboxChange}
                        >
                            <Text fontSize="sm" color="gray.600" m={0}>
                                Saya bukan robot
                            </Text>
                        </Checkbox>

                        {/* ✅ Captcha slider muncul setelah checkbox diklik */}
                        <SliderCaptcha
                            isOpen={isCaptchaVisible}
                            onSuccess={handleCaptchaSuccess}
                        />

                        <Button
                            type="submit"
                            h="40px"
                            fontSize="sm"
                            isLoading={isSubmitting}
                            colorScheme="brand"
                            borderRadius="md"
                            isDisabled={!validate}
                        >
                            Masuk
                        </Button>

                        <Link
                            href="/forgot-password"
                            fontSize="sm"
                            color="red.500"
                            textAlign="center"
                            _hover={{ textDecoration: "underline" }}
                        >
                            Lupa Kata Sandi
                        </Link>
                    </Stack>
                </Box>
            </Flex>
        </Flex>
    );
};

export default LoginForm;
